/*
  How It Works
  1. User enters/updates a search query
  2. Input change event triggered from parent class "SearchForm"
  3. Get the search query from input value
  4. If search query is empty, clear the results
  5. If search query is not empty, get the results
  6. Parse, cache, and insert the results from Shopify's retrieval of the predictive search results section 'predictive-search-results.liquid'
  
  Events/Actions
  1. On form reset event, trigger reset from parent class "SearchForm" and clear the results
  2. On focus, check if updates to search query
  3. On focus out, if no element is selected by user then close results
  4. On form submit event, prevent submitting of form if user has element selected

  Accesibilty
  1. When searching for results, update the live region state to indicate loading
  2. When results are inserted, set the live region state to indicate results
  3. Check key controls to maneuver selection of elements in results
  4. Prevent the cursor from moving in the input when using the up and down arrow keys
  5. Set aria-selected="true" for the selected/highlighted element
  6. Set aria-activedescendant attribute on the input since input is in focus but not the currently active element
  7. When filter button is clicked, set aria-checked to true

  Filters
  1. On search, two fetches are performed for the results JSON and markup
  2. When a user clicks on a filter button, see if already checked to add/remove the filter
  4. Iterate through the JSON cache with the existing checked filters
  3. If product does not have the button's tag identifier, hide the product
*/

class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.searchTerm = "";
    this.abortController = new AbortController();
    this.input = this.querySelector('input[type="search"]');
    //this.inputContainer = this.querySelector('.search-input-container');
    this.closeBtn = this.querySelector(".close-search");
    this.searchResultsContainer = this.querySelector(".search-results-container");

    // Filters
    this.currentFilters = [];
    this.searchFilters = [];

    // Total results count
    this.totalResultsCount = 0;

    // Cache search results to minimize search load
    this.cachedResults = {};
    this.cachedResultsJSON = {};
    this.cachedFilteredResults = {};

    // Predictive search results container
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    this.closeBtn.addEventListener("click", () => {
      this.closeResults(true);
      this.onChange();
    });
  }

  onFormSubmit(e) {
    // Prevent submitting of form if user has element selected
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) e.preventDefault();
  }

  async onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.openResults();
    } else {
      await this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeResults(true);
    });
  }

  // Check key controls to maneuver selection of elements
  onKeyup(e) {
    if (!this.getQuery().length) this.closeResults(true);
    e.preventDefault();

    switch (e.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        // Simulate user click
        this.selectOption();
        break;
    }
  }

  onKeydown(e) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (e.code === "ArrowUp" || e.code === "ArrowDown") {
      e.preventDefault();
    }
  }

  // Maneuver selection of elements
  switchOption(direction) {
    // If no results, then return
    if (!this.getAttribute("open")) return;

    // See if up or down arrow key
    const moveUp = direction === "up";

    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll("li, button.predictive-search__item")).filter(
      (el) => el.offsetParent !== null && !el.classList.contains("remove-from-dom")
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  // Simulate user click if element is selected
  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.closeResults(true);
      return;
    }

    await this.getSearchResults(this.searchTerm);
  }

  getQuery() {
    return this.input.value.trim();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.removeAttribute("data-has-results");
      this.totalResultsCount = 0;
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.removeAttribute("style");

    // Clear the results
    this.predictiveSearchResults.querySelector(".search-results-container").innerHTML = "";
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    // Set live region state to indicate loading
    this.setLiveRegionLoadingState();

    // Render the cached results
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      const searchParams = `&resources[type]=product,query,collection&resources[options][fields]=title,product_type,variants.title,variants.barcode,variants.sku,vendor,tag&section_id=predictive-search-results`;

      // Fetch results JSON
      const resJSON = await fetch(
        window.Shopify.routes.root + `search/suggest.json?q=${encodeURIComponent(searchTerm) + searchParams}`,
        {
          signal: this.abortController.signal,
        }
      );

      if (!resJSON.ok) {
        const error = new Error(resJSON.status);
        this.closeResults();
        throw error;
      }
      const suggestions = await resJSON.json();
      const productSuggestions = suggestions.resources.results.products;
      if (productSuggestions.length > 0) {
        // Cache the JSON
        this.cachedResultsJSON[queryKey] = productSuggestions;
      }

      // Fetch results markup
      const resMarkup = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm) + searchParams}`,
        {
          signal: this.abortController.signal,
        }
      );

      if (!resMarkup.ok) {
        const error = new Error(resMarkup.status);
        this.closeResults();
        throw error;
      }

      const text = await resMarkup.text();

      // Parse results from Shopify's retrieval of the predictive search results section 'predictive-search-results.liquid'
      const resultsMarkup = new DOMParser()
        .parseFromString(text, "text/html")
        .querySelector("#shopify-section-predictive-search-results").innerHTML;

      // Cache the results
      this.cachedResults[queryKey] = resultsMarkup;

      // Render the results in the search modal
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error?.code === 20) {
        // Code 20 means the call was aborted
        return;
      }
      this.closeResults();
      throw error;
    }
  }

  // Set the live region state to indicate loading
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  // Set the live region text to indicate status
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Insert the results
    this.predictiveSearchResults.querySelector(".search-results-container").innerHTML = resultsMarkup;

    // Rerender nonfunctional scripts from parsed results
    this.injectScripts(this.predictiveSearchResults.querySelector(".search-results-container"));

    const queryKey = this.searchTerm.replace(" ", "-").toLowerCase();

    this.resetFilters();

    this.searchFilters = this.querySelectorAll(".search-filter.filter-button");

    this.searchFilters.forEach((filter) => {
      // Add check listener
      filter.addEventListener("click", () =>
        this.updateFilters(
          filter.getAttribute("data-tag-identifier"),
          filter.getAttribute("aria-checked") === "true",
          filter
        )
      );

      // Get tag count in JSON results
      const filterTag = filter.getAttribute("data-tag-identifier");
      if (!filterTag) return;

      const productsWithTag = this.cachedResultsJSON[queryKey].filter(({ tags }) => tags.indexOf(filterTag) !== -1);

      // Show tag count
      if (productsWithTag.length !== 0) {
        filter.querySelector(".filter-count").innerHTML = productsWithTag.length;
        filter.nextElementSibling.innerHTML = `${productsWithTag.length} result${
          productsWithTag.length > 1 ? "s" : ""
        } available that matches filter`;
      }
    });

    // Set the live region state to indicate results
    this.setAttribute("results", true);
    this.setAttribute("data-has-results", true);
    this.setLiveRegionResults();

    // Show the results
    this.openResults();
  }

  resetFilters() {
    this.currentFilters = [];
    this.searchFilters = [];
    this.totalResultsCount = 0;
    this.cachedFilteredResults = {};
  }

  updateFilters(value, checked = false, el) {
    if (!value) return;

    // Toggle filters in/out of array
    if (!checked) {
      this.currentFilters.push(value);
      this.filterResults(true);

      // Indicate checked
      el.setAttribute("aria-checked", "true");
    } else {
      this.currentFilters = this.currentFilters.filter((filter) => filter !== value);
      this.filterResults(false);

      // Remove checked
      el.setAttribute("aria-checked", "false");
    }
  }

  filterResults(filterDown = true) {
    const queryKey = this.searchTerm.replace(" ", "-").toLowerCase();

    // Filter through the already filtered results for better performance
    const results =
      filterDown && this.cachedFilteredResults[queryKey]
        ? this.cachedFilteredResults[queryKey]
        : this.cachedResultsJSON[queryKey];

    const filteredResults = results.filter(({ id, tags }) => {
      const showCard =
        this.currentFilters.length === 0
          ? true
          : tags.some(
              (tag) =>
                !tags.includes("hidden") &&
                (this.currentFilters.length === 0 ? true : this.currentFilters.indexOf(tag) !== -1)
            );

      const productCard = document.querySelector(`[data-product-id="${id}"]`);

      if (showCard) {
        productCard?.parentElement?.classList.remove("tw-hidden");
        return true;
      } else {
        // Emulate removal from DOM without actually removing element from DOM
        productCard?.parentElement?.classList.add("tw-hidden");
        return false;
      }
    });

    this.totalResultsCount = filteredResults.length;
    this.cachedFilteredResults[queryKey] = filteredResults;
  }

  // Reinject script tags to allow execution
  injectScripts(el) {
    el.querySelectorAll("script").forEach((oldScriptTag) => {
      // By default, scripts are disabled when using element.innerHTML
      const newScriptTag = document.createElement("script");

      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });

      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  // Set the live region state to indicate loading
  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(this.querySelector("[data-predictive-search-live-region-count-value]").textContent);
  }

  // Show the results
  openResults() {
    // Get the max height for the results
    const setResultsHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
    this.style.setProperty("--search-results-height", `${setResultsHeight}px`);

    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    this.searchResultsContainer.classList.replace("tw-opacity-0", "tw-opacity-1");
    this.searchResultsContainer.classList.replace("tw-invisible", "tw-visible");
  }

  // Get the max height for the results
  getResultsMaxHeight() {
    const headerBottom = getComputedStyle(document.body).getPropertyValue("--header-bottom");

    this.inputContainer = this.querySelector(".search-input-container");

    this.resultsMaxHeight =
      window.innerHeight - (parseInt(headerBottom.replace("px", "")) + this.inputContainer.offsetHeight + 1);

    return this.resultsMaxHeight;
  }

  // Form reset event from parent class "SearchForm"
  onFormReset(e) {
    super.onFormReset(e);
    if (!super.shouldResetForm()) return;

    // Reset PredictiveSearch class
    this.searchTerm = "";
    this.abortController.abort();
    this.abortController = new AbortController();

    // Close results
    this.closeResults(true);
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}
